<template>
  <div 
    ref="containerRef"
    class="lazy-field-container"
    :class="{ 'is-loading': !isLoaded, 'is-loaded': isLoaded }"
  >
    <!-- Placeholder avant chargement -->
    <div 
      v-if="!isLoaded" 
      class="lazy-field-placeholder link-placeholder"
      @click="loadComponent"
    >
      <div class="placeholder-content">
        <i class="fa fa-link placeholder-icon"></i>
        <p class="placeholder-text">Cliquer pour charger le lien</p>
        <div v-if="linkPreview" class="placeholder-preview">
          <div class="preview-content">{{ linkPreview }}</div>
        </div>
      </div>
    </div>

    <!-- Composant chargé -->
    <BaseLink
      v-if="isLoaded"
      ref="baseComponentRef"
      v-bind="$props"
      @update:model-value="$emit('update:modelValue', $event)"
      @click="$emit('click', $event)"
      @activate-group="$emit('activate-group', $event)"
      @deactivate-group="$emit('deactivate-group', $event)"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted, onBeforeUnmount, ref, nextTick } from 'vue';
import BaseLink from './BaseLink.vue';

export default defineComponent({
  name: 'LazyBaseLink',

  components: {
    BaseLink,
  },

  props: {
    // Hériter de toutes les props de BaseLink
    modelValue: {
      type: [String, Object],
      required: true,
    },
    variablesList: {
      type: Array,
      required: false,
      default: () => [],
    },
    linkTypeOptions: {
      type: Array,
      required: true,
    },
    originCall: {
      type: String,
      required: false,
      default: null,
    },
    idCampaign: {
      type: Number,
      required: false,
      default: null,
    },
    translation: {
      type: Object,
      required: false,
      default: null,
    },
    // Props spécifiques au lazy loading
    loadMode: {
      type: String,
      required: false,
      default: 'auto',
      validator: (value: string) => ['auto', 'click', 'immediate'].includes(value),
    },
    intersectionThreshold: {
      type: Number,
      required: false,
      default: 0.1,
    },
    forceLoad: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['update:modelValue', 'click', 'activate-group', 'deactivate-group'],

  setup(props) {
    const containerRef = ref<HTMLElement>();
    const isLoaded = ref(false);
    let observer: IntersectionObserver | null = null;

    // Aperçu du lien
    const linkPreview = computed(() => {
      if (!props.modelValue) return '';
      
      if (typeof props.modelValue === 'string') {
        return props.modelValue.length > 50 ? `${props.modelValue.substring(0, 50)}...` : props.modelValue;
      }
      
      if (typeof props.modelValue === 'object') {
        if (props.modelValue.url) {
          const url = props.modelValue.url;
          return url.length > 50 ? `${url.substring(0, 50)}...` : url;
        }
        if (props.modelValue.text) {
          const text = props.modelValue.text;
          return text.length > 50 ? `${text.substring(0, 50)}...` : text;
        }
      }
      
      return 'Lien configuré';
    });

    const loadComponent = () => {
      if (!isLoaded.value) {
        isLoaded.value = true;
        if (observer) {
          observer.disconnect();
          observer = null;
        }
      }
    };

    const setupIntersectionObserver = () => {
      if (!containerRef.value || typeof IntersectionObserver === 'undefined') {
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= props.intersectionThreshold) {
              loadComponent();
            }
          });
        },
        {
          threshold: props.intersectionThreshold,
          rootMargin: '50px',
        }
      );

      observer.observe(containerRef.value);
    };

    onMounted(() => {
      if (props.forceLoad || props.loadMode === 'immediate') {
        loadComponent();
      } else if (props.loadMode === 'auto') {
        nextTick(() => {
          setupIntersectionObserver();
        });
      }
    });

    onBeforeUnmount(() => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    });

    // Référence vers le composant chargé
    const baseComponentRef = ref();

    // Méthode focus qui redirige vers le composant chargé
    const focus = () => {
      if (isLoaded.value && baseComponentRef.value && baseComponentRef.value.focus) {
        baseComponentRef.value.focus();
      } else if (!isLoaded.value) {
        // Si pas encore chargé, charger d'abord puis focus
        loadComponent();
        nextTick(() => {
          if (baseComponentRef.value && baseComponentRef.value.focus) {
            baseComponentRef.value.focus();
          }
        });
      }
    };

    // Méthodes pour contrôler l'Intersection Observer pendant le scroll
    const pauseObserver = () => {
      if (observer) {
        observer.disconnect();
      }
    };

    const resumeObserver = () => {
      if (!isLoaded.value && containerRef.value) {
        setupIntersectionObserver();
      }
    };

    return {
      containerRef,
      isLoaded,
      loadComponent,
      linkPreview,
      baseComponentRef,
      focus, // Exposer focus via return
      pauseObserver, // Exposer pour contrôler l'observer
      resumeObserver, // Exposer pour contrôler l'observer
    };
  },
});
</script>

<style scoped lang="scss">
.lazy-field-container {
  min-height: 50px;
  transition: all 0.3s ease;

  &.is-loading {
    border: 1px dashed #e0e0e0;
    border-radius: 4px;
  }

  &.is-loaded {
    border: none;
  }
}

.lazy-field-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50px;
  padding: 0.5rem;
  cursor: pointer;
  background: #fafafa;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: #f0f0f0;
    border-color: #ccc;
  }

  .placeholder-content {
    text-align: center;
    max-width: 100%;
  }

  .placeholder-icon {
    font-size: 1.2rem;
    color: #999;
    margin-bottom: 0.25rem;
  }

  .placeholder-text {
    color: #666;
    font-size: 0.75rem;
    margin: 0 0 0.25rem 0;
    font-style: italic;
  }

  .placeholder-preview {
    max-width: 100%;
    margin-top: 0.25rem;
    
    .preview-content {
      background: white;
      border: 1px solid #ddd;
      border-radius: 3px;
      padding: 0.25rem 0.5rem;
      font-size: 0.7rem;
      color: #555;
      text-align: left;
      max-height: 30px;
      overflow: hidden;
      line-height: 1.2;
    }
  }

  &.link-placeholder {
    min-height: 50px;
    .placeholder-icon {
      font-size: 1.2rem;
    }
  }
}
</style>
